import { type GridListComponentMapProps, GridListLayout } from "@client/content/controls/GridList/component";
import { makeModuleStateSelector, rootContainerPropsSelector } from "@client/content/selectors";
import { globals } from "@client/globals";
import { getMultiselectValue } from "@client/lib/settings";
import {
  TargetSelector,
  combinedSettingsSelector,
  createShallowEqualSelector,
  getSettingValueSelector,
  portalIdSelector
} from "@client/selectors";
import classnames from "classnames";
import { createSelector } from "reselect";
import { eventIdSelector } from "../selectors";
import { type ProductListItemProps } from "./Content/Item/component";
import ProductListItem from "./Content/Item/container";
import { defaultState, previewState } from "./reducer";
import { ProductList } from "./types";

export const localStateSelector = makeModuleStateSelector<ProductList.State>(defaultState, previewState);

export const localStateCategoriesSelector = createSelector([localStateSelector], (ls) => ls.categories);

export const apiUrlSelector = createSelector([], () => globals.paths.apiUrl);

export const makeItemsIdsSelector = () =>
  createSelector([localStateSelector], (localState) => localState.items.map((item) => item.id));

export const makeFiltersSelector = () => createSelector([localStateSelector], (locateState) => locateState.filters);

export const makeCategoryIdSelector = () => {
  const categorySettingsSelector = getSettingValueSelector(TargetSelector.Entity, "category");
  const filtersSelector = makeFiltersSelector();

  return createSelector([filtersSelector, categorySettingsSelector], (filters, category) => {
    const categories = getMultiselectValue(category);
    if (categories?.length === 1) return categories[0];

    return filters.categoryId;
  });
};

export const makeFilterOrderBySelector = () => {
  const orderSelector = getSettingValueSelector(TargetSelector.Entity, "order");
  return createSelector([localStateSelector, orderSelector], (localState, order) => {
    const finalOrder = localState.filters.order || order;
    return localState.orders.length
      ? localState.orders.some((c) => c.value === finalOrder)
        ? finalOrder
        : localState.orders[0]?.value
      : finalOrder;
  });
};

export const makeFilterPageSizeSelector = () => getSettingValueSelector(TargetSelector.Entity, "pageSize");

export const makeFilterSearchTextSelector = () => {
  return createSelector([localStateSelector], (localState) => localState.filters.searchText);
};

export const makeGridListPropsSelector = () => {
  const itemsIdsSelector = makeItemsIdsSelector();

  const subSettingsSelector = combinedSettingsSelector(TargetSelector.Entity, [
    "columns",
    { name: "orientation", prop: "imageOrientation", path: "image" },
    "isMasonry",
    "pageSize"
  ]);

  return createShallowEqualSelector(
    [localStateSelector, subSettingsSelector, itemsIdsSelector, rootContainerPropsSelector],
    (
      localState,
      settings,
      itemIds,
      { id, ...rootContainerProps }
    ): GridListComponentMapProps<ProductListItemProps> => ({
      className: classnames({
        "product-thumbnails": settings.imageOrientation === "left",
        "product-thumbnails product-thumbnails-img-right": settings.imageOrientation === "right"
      }),
      isLoading: (localState.status !== "Ready" && localState.status !== "AddingToCart") || localState.isFetchingPage,
      page: localState.page,
      items: itemIds,
      columns: settings.columns,
      gutter: 20,
      layout: (settings.isMasonry ? "Masonry" : "Grid") as GridListLayout,
      pageSize: settings.pageSize,
      hasMore: localState.items.length === settings.pageSize,
      itemProps: {
        ...rootContainerProps,
        parentId: id
      },
      ItemComponent: ProductListItem
    })
  );
};

export const makeProductPageRequestSelector = (page: number) => {
  const categoryIdSelector = makeCategoryIdSelector();
  const categorySettingsSelector = getSettingValueSelector(TargetSelector.Entity, "category");
  const pageSizeSelector = makeFilterPageSizeSelector();
  const orderSelector = makeFilterOrderBySelector();
  const searchTextSelector = makeFilterSearchTextSelector();

  return createSelector(
    [
      portalIdSelector,
      categoryIdSelector,
      categorySettingsSelector,
      pageSizeSelector,
      orderSelector,
      eventIdSelector,
      searchTextSelector
    ],
    (portalId, category, categories, size, sortBy, eventId, searchText): ProductList.Api.GetProductPageRequest => ({
      portalId,
      productCategoryIds: category ? [category] : getMultiselectValue(categories),
      eventId: eventId ?? null,
      page,
      size,
      sortBy,
      order: "asc",
      search: searchText
    })
  );
};
